/** Remembers that the device is a terminal, so the app opens the terminal screen (no data, T-173). */
export interface TerminalMarker {
  set(paired: boolean): void;
  get(): boolean;
}

/** Key of the marker in local storage. */
export const terminalMarkerKey = "zeitavo.terminal";

/**
 * Creates the marker on top of local storage.
 *
 * @param storage - The storage, or `null` where it is blocked.
 * @returns The marker.
 */
export function storageMarker(storage: Storage | null): TerminalMarker {
  const marker: TerminalMarker = {
    set: (paired) => {
      try {
        if (paired) {
          storage?.setItem(terminalMarkerKey, "1");
        } else {
          storage?.removeItem(terminalMarkerKey);
        }
      } catch {
        // Without storage the app simply starts at its start page.
      }
    },
    get: () => {
      try {
        const paired = storage?.getItem(terminalMarkerKey) === "1";
        return paired;
      } catch {
        return false;
      }
    },
  };

  return marker;
}

/**
 * Returns the local storage, or `null` where it is blocked.
 *
 * @param scope - The window.
 * @returns The storage.
 */
export function localStorageOf(scope: Pick<Window, "localStorage">): Storage | null {
  try {
    const storage = scope.localStorage;
    return storage;
  } catch {
    return null;
  }
}

/**
 * Returns the path the app opens: a device paired as terminal opens `/terminal` instead of the start page, so it never
 * asks a person to sign in (T-173).
 *
 * @param pathname - The path the app was opened with.
 * @param marker - The marker of the device.
 * @returns The path to open, or `null` to keep the current one.
 */
export function terminalStartPath(pathname: string, marker: TerminalMarker): string | null {
  const path = pathname === "/" && marker.get() ? "/terminal" : null;
  return path;
}
